import { Anchor, Badge, Box, Button, Drawer, Group, Mark, Paper, Progress, Stack, Text } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { Id, formatDate } from '@feature/utils';

import { getTravelRequestOptions, getTravelRequestOverlapsOptions } from '../model/TravelRequestQuery';
import { TravelOverlap, formatTravelRegistration } from '../model/TravelRequestTypes';
import { TravelOverlapSource, TravelOverlapWord, diffWords, sameWords } from './TravelOverlapUtils';
import { TravelRegistryTypeBadge } from './TravelRegistryTypeBadge';
import { TravelRequestStatusBadge } from './TravelRequestStatusBadge';

interface TravelOverlapDrawerProps {
  requestId: Id;
  opened: boolean;
  onClose: () => void;
  // Fetched on open when the caller does not already hold the request.
  source?: TravelOverlapSource;
}

type TravelOverlapContentProps = Pick<TravelOverlapDrawerProps, 'requestId' | 'source' | 'onClose'>;

function Words({ words }: { words: TravelOverlapWord[] }) {
  return (
    <Text size="sm">
      {words.map(word => (
        <Fragment key={word.start}>
          {word.lead}
          {word.marked ? <Mark color="yellow">{word.core}</Mark> : word.core}
          {word.trail}{' '}
        </Fragment>
      ))}
    </Text>
  );
}

function TravelOverlapComparison({ own, other }: { own: string | null; other: string }) {
  const { t } = useTranslation();
  const rows: [string, TravelOverlapWord[]][] = [];

  if (own == null || sameWords(own, other)) {
    rows.push(['travel.overlaps.otherRequest', diffWords(other, other)[0]]);
  } else {
    const [ownWords, otherWords] = diffWords(own, other);
    rows.push(['travel.overlaps.thisRequest', ownWords], ['travel.overlaps.otherRequest', otherWords]);
  }

  return (
    <Box
      style={{ display: 'grid', gridTemplateColumns: '72px minmax(0, 1fr)', rowGap: 6, columnGap: 8 }}
      aria-label={t('travel.overlaps.comparison')}
      role="group"
    >
      {rows.map(([label, words]) => (
        <Fragment key={label}>
          <Text
            size="xs"
            c="dimmed"
            pt={2}
          >
            {t(label)}
          </Text>
          <Words words={words} />
        </Fragment>
      ))}
    </Box>
  );
}

interface TravelOverlapCardProps {
  overlap: TravelOverlap;
  source?: TravelOverlapSource;
  onNavigate: () => void;
}

export function TravelOverlapErrorLine({
  message = 'travel.overlaps.error',
  onRetry,
}: {
  message?: string;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Group gap="xs">
      <Text
        size="sm"
        c="dimmed"
      >
        {t(message)}
      </Text>
      <Button
        size="compact-xs"
        variant="subtle"
        onClick={onRetry}
      >
        {t('travel.overlaps.retry')}
      </Button>
    </Group>
  );
}

function TravelOverlapCard({ overlap, source, onNavigate }: TravelOverlapCardProps) {
  const { t } = useTranslation();
  const { exact, similarity, request } = overlap;
  const { registrationNumber, registrationYear, registrationDate, registryType, paperTitle } = request;
  const registration =
    registrationNumber != null && registrationYear != null
      ? formatTravelRegistration({ registrationNumber, registrationYear, registrationDate })
      : t('travel.overlaps.unregistered');
  const dates = `${formatDate(request.departureDate) ?? '-'} - ${formatDate(request.returnDate) ?? '-'}`;
  // Only an exact match may read 100%.
  const percentage = exact ? 100 : Math.min(99, Math.round(similarity * 100));

  return (
    <Paper
      component="article"
      withBorder
      radius="md"
      p="sm"
    >
      <Stack gap="xs">
        <Group
          justify="space-between"
          wrap="nowrap"
          align="flex-start"
        >
          <Stack
            gap={4}
            align="flex-start"
            miw={0}
          >
            <Anchor
              component={Link}
              to={`/travel/requests/${request.id}`}
              size="sm"
              fw={600}
              onClick={onNavigate}
            >
              {`${registration} · ${request.requester.name}`}
            </Anchor>
            {registryType != null && <TravelRegistryTypeBadge registryType={registryType} />}
          </Stack>
          <TravelRequestStatusBadge status={request.status} />
        </Group>
        {exact && (
          <Badge
            color="red"
            variant="light"
          >
            {t('travel.overlaps.exact')}
          </Badge>
        )}
        <Group
          gap="xs"
          wrap="nowrap"
        >
          <Progress
            value={percentage}
            color={exact ? 'red.6' : 'yellow.5'}
            size="sm"
            flex={1}
            aria-label={t('travel.overlaps.similarity')}
          />
          <Text
            size="sm"
            fw={600}
          >
            {percentage}%
          </Text>
        </Group>
        <Text
          size="xs"
          c="dimmed"
        >
          {dates}
        </Text>
        {source != null && paperTitle != null && (
          <TravelOverlapComparison
            own={source.paperTitle}
            other={paperTitle}
          />
        )}
      </Stack>
    </Paper>
  );
}

function TravelOverlapContent({ requestId, source, onClose }: TravelOverlapContentProps) {
  const { t } = useTranslation();
  const { data, isError, refetch } = useQuery(getTravelRequestOverlapsOptions(requestId));
  const fetched = useQuery({ ...getTravelRequestOptions(requestId), enabled: source == null });
  const compareWith = source ?? fetched.data;
  let sourceMessage: string | null = null;
  if (source == null && !fetched.isError && fetched.isPending) {
    sourceMessage = 'travel.overlaps.sourceLoading';
  }

  if ((data == null || data.length === 0) && isError) {
    return <TravelOverlapErrorLine onRetry={() => refetch()} />;
  }

  if (data == null || data.length === 0) {
    return (
      <Text
        size="sm"
        c="dimmed"
      >
        {t(data == null ? 'travel.overlaps.loading' : 'travel.overlaps.empty')}
      </Text>
    );
  }

  return (
    <Stack gap="sm">
      {source == null && fetched.isError && (
        <TravelOverlapErrorLine
          message="travel.overlaps.sourceError"
          onRetry={() => fetched.refetch()}
        />
      )}
      {sourceMessage != null && (
        <Text
          size="sm"
          c="dimmed"
        >
          {t(sourceMessage)}
        </Text>
      )}
      {data.map(overlap => (
        <TravelOverlapCard
          key={overlap.request.id}
          overlap={overlap}
          source={compareWith}
          onNavigate={onClose}
        />
      ))}
      {isError && <TravelOverlapErrorLine onRetry={() => refetch()} />}
      <Text
        size="xs"
        c="dimmed"
      >
        {t('travel.overlaps.footnote')}
      </Text>
    </Stack>
  );
}

export function TravelOverlapDrawer({ requestId, opened, onClose, source }: TravelOverlapDrawerProps) {
  const { t } = useTranslation();
  const narrow = useMediaQuery('(max-width: 48em)');

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={narrow ? '100%' : 460}
      title={t('travel.overlaps.drawerTitle')}
      closeButtonProps={{ 'aria-label': t('button.close') }}
    >
      {opened && (
        <TravelOverlapContent
          key={requestId}
          requestId={requestId}
          source={source}
          onClose={onClose}
        />
      )}
    </Drawer>
  );
}
